import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { trackEvent } from "../lib/tracking";
import { CALENDLY_URL } from "../lib/contact";

const TELL_ME = [
  "Tell me what bedtime looked like.",
  "Tell me when they woke.",
  "Tell me what you did.",
  "Tell me what happened next.",
  "Tell me what’s been happening with naps.",
  "Tell me what you’ve already tried.",
  "Tell me the thing you keep wondering if you’re doing wrong.",
];

export const Assessment = ({ onStart }) => (
  <section id="free-assessment" data-testid="free-assessment-section" className="bg-creamsoft">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-6">
        <Reveal>
          <Label>Let&rsquo;s start with last night.</Label>
        </Reveal>
        <Reveal as="h2" delay={0.08} className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
          You don&rsquo;t need
          <span className="block">to know what&rsquo;s wrong</span>
          <span className="block italic text-clay">before we talk.</span>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-8 font-serif italic font-light text-xl sm:text-2xl text-ink/70 leading-snug">
            Seriously. That&rsquo;s not your job.
          </p>
          <div className="mt-6 space-y-3.5 max-w-md">
            {TELL_ME.map((t) => (
              <p key={t} className="flex items-baseline gap-4 text-base sm:text-lg font-normal text-smoke">
                <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-5px]" />
                {t}
              </p>
            ))}
          </div>
          <p className="mt-8 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug">
            I&rsquo;ll start there.
          </p>
        </Reveal>
      </div>

      <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
        <Reveal delay={0.1}>
          <h3 className="font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.12] tracking-tight">
            Start with a complimentary
            <span className="block italic text-clay">pediatric sleep assessment.</span>
          </h3>
          <p
            data-testid="assessment-value"
            className="mt-5 font-serif font-light text-xl sm:text-2xl leading-snug text-ink"
          >
            A <span className="italic text-clay">$149</span> professional
            assessment — <span className="italic text-clay">complimentary.</span>
          </p>
          <div className="mt-6 space-y-4 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-md">
            <p>
              I personally set this time aside to understand what&rsquo;s
              happening with your child&rsquo;s sleep — what bedtime, nights
              and naps actually look like, what you&rsquo;ve already tried, and
              where things may be getting stuck.
            </p>
            <p>
              By the end of your assessment, you&rsquo;ll have a clearer
              understanding of what&rsquo;s happening, what I recommend, and
              what the next step could look like for your family.
            </p>
            <p>
              The personalized sleep plan, implementation, and continued
              support are separate professional services. If you&rsquo;d like
              that continued support, I&rsquo;ll walk you through the approach
              I recommend, what&rsquo;s included, and what working together
              would look like. There is never any obligation to continue.
            </p>
          </div>
          <p className="mt-8 font-serif italic font-light text-xl sm:text-2xl text-clay leading-snug">
            That&rsquo;s what the assessment is for.
          </p>
          <a
            data-testid="assessment-cta"
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent("cta_click", { location: "free_assessment" });
              trackEvent("book_assessment_click", { location: "free_assessment" });
            }}
            className="mt-9 inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300 group"
          >
            Book my complimentary pediatric sleep assessment
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <p data-testid="assessment-partner" className="mt-6 text-sm font-normal leading-relaxed text-smoke max-w-md">
            If someone else will be part of the decision about moving forward,
            they&rsquo;re welcome to join your assessment.
          </p>
          <p data-testid="assessment-time" className="mt-3 text-sm font-normal leading-relaxed text-smoke/80 max-w-md">
            I personally set this time aside for your family — please reserve a
            time when you&rsquo;re able to attend and genuinely considering
            professional sleep support.
          </p>
          <p className="mt-7 text-sm font-normal text-smoke">
            Rather tell me what&rsquo;s happening first?{" "}
            <button
              data-testid="assessment-tell-cta"
              onClick={() => {
                trackEvent("cta_click", { location: "free_assessment_tell" });
                onStart();
              }}
              className="inline-flex items-center gap-1.5 text-ink underline underline-offset-8 decoration-ink/25 hover:decoration-clay hover:text-claydark transition-[text-decoration-color,color] duration-300"
            >
              Tell Andrea what&rsquo;s going on
              <ArrowRight size={13} />
            </button>
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
